import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Alert, Box, Button, Typography, useTheme, Drawer, useMediaQuery, AppBar, Toolbar, FormControlLabel, Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid2';
import { CheckOutlined } from '@mui/icons-material';
import { Icon } from '@iconify/react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEditDashboardStyles } from './useEditColumnDialogStyles';
import CustomDialog from '@ui/components/common/Dialog';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getAllResponsesLabelsByEventIdAsync, updateResponseGridLabelsAsync } from '@application/features/vdkEventResponses/vdkEventResponsesThunk';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { APIStatus } from '@application/enums/api-status';
import { toast } from 'react-toastify';
import { SiDragIndicatorDuotone } from '@ui/assets/custom-icons/drag-icon';
const EditColumnDialog = ({ open, onClose, onRefreshGridData }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const styles = useEditDashboardStyles();
    const dispatch = useAppDispatch();
    const { eventId } = useParams();
    const { allResponsesLabelsByEventId, status } = useAppSelector((state) => state.vdkEventResponse);
    const [localGridLabels, setLocalGridLabels] = useState([]);
    // Fetch all labels when dialog opens
    useEffect(() => {
        if (open && eventId) {
            dispatch(getAllResponsesLabelsByEventIdAsync(eventId));
        }
    }, [open, eventId, dispatch]);
    // Update local state when API data changes
    useEffect(() => {
        if (allResponsesLabelsByEventId) {
            setLocalGridLabels([...allResponsesLabelsByEventId]);
        }
    }, [allResponsesLabelsByEventId]);
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: 8
        }
    }), useSensor(TouchSensor, {
        activationConstraint: {
            delay: 200,
            tolerance: 8
        }
    }), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates
    }));
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = localGridLabels.findIndex((label) => label.id === active.id);
            const newIndex = localGridLabels.findIndex((label) => label.id === over?.id);
            if (oldIndex !== -1 && newIndex !== -1) {
                const newLabels = arrayMove(localGridLabels, oldIndex, newIndex);
                // Update fieldSortOrderInResponseGrid based on new position
                const updatedLabels = newLabels.map((label, index) => ({
                    ...label,
                    fieldSortOrderInResponseGrid: index
                }));
                setLocalGridLabels(updatedLabels);
            }
        }
    };
    const handleToggleField = (labelId) => {
        setLocalGridLabels((prev) => prev.map((label) => label.id === labelId
            ? { ...label, isFieldInResponsesGrid: !label.isFieldInResponsesGrid }
            : label));
    };
    const handleSaveChanges = async () => {
        if (eventId) {
            const request = {
                eventId,
                requestUpdateResponseGridTitlesDtos: localGridLabels
            };
            try {
                await dispatch(updateResponseGridLabelsAsync(request)).unwrap();
                onClose();
                // Refresh grid data after successful update
                if (onRefreshGridData) {
                    onRefreshGridData();
                }
                toast.success(t('events.eventDetails.responsesTab.columnsUpdatedSuccessfully'));
            }
            catch (error) {
                console.error('Failed to update grid labels:', error);
            }
        }
    };
    // Sortable Field Component
    const SortableField = ({ label }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
            id: label.id
        });
        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : 1
        };
        return (_jsx(Box, { ref: setNodeRef, style: style, sx: styles.getStepBoxStyle(label.isFieldInResponsesGrid), children: _jsxs(Box, { sx: {
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }, children: [_jsxs(Box, { display: 'flex', justifyContent: isMobile ? undefined : 'space-between', gap: 1, width: '100%', alignItems: 'center', children: [_jsx(Box, { ...attributes, ...listeners, sx: {
                                    cursor: 'grab',
                                    '&:active': { cursor: 'grabbing' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '4px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        borderRadius: '4px'
                                    }
                                }, children: _jsx(SiDragIndicatorDuotone, {}) }), _jsx(Typography, { variant: 'body1', fontWeight: 400, sx: { width: 'calc(100% - 40px)' }, children: label.label })] }), _jsx(FormControlLabel, { control: _jsx(Switch, { checked: label.isFieldInResponsesGrid ? true : false, onClick: () => handleToggleField(label.id) }), label: t('events.eventDetails.dashboardTab.show') })] }) }));
    };
    const dialogContent = (_jsx(Box, { children: _jsxs(Grid, { container: true, spacing: 2.5, p: 3, children: [_jsx(Grid, { size: 12, children: _jsx(Alert, { variant: 'outlined', severity: 'info', icon: _jsx(Icon, { icon: 'eva:info-fill', width: 24, height: 24 }), sx: styles.alertStyle, children: t('events.eventDetails.responsesTab.editColumnsAlert') }) }), _jsx(Grid, { size: 12, children: _jsx(Box, { display: 'flex', flexDirection: 'column', gap: 1, children: !localGridLabels || localGridLabels.length === 0 ? (_jsx(Box, { sx: styles.getStepBoxStyle(false), children: _jsx(Typography, { variant: 'body1', fontWeight: 400, children: t('events.eventDetails.responsesTab.noFields') }) })) : (_jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, children: _jsx(SortableContext, { items: localGridLabels.map((label) => label.id), strategy: verticalListSortingStrategy, children: localGridLabels.map((label) => (_jsx(SortableField, { label: label }, label.id))) }) })) }) })] }) }));
    const actionButtons = (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', onClick: onClose, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', endIcon: _jsx(CheckOutlined, {}), onClick: handleSaveChanges, disabled: status === APIStatus.PENDING, children: t('actions.saveChanges') })] }));
    if (isMobile) {
        return (_jsxs(Drawer, { anchor: 'bottom', open: open, onClose: onClose, PaperProps: { sx: styles.mobileDrawerStyle }, children: [_jsx(AppBar, { position: 'sticky', elevation: 0, sx: styles.appBarStyle, children: _jsx(Toolbar, { sx: styles.mobileToolbarStyle, children: _jsx(Box, { display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', children: _jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    width: '100%',
                                    justifyContent: 'center'
                                }, gap: 0.5, children: [_jsx("img", { src: '/assets/images/dialog-pencil.png', width: 60, height: 60, alt: 'pencilIcon' }), _jsx(Typography, { variant: 'h5', fontWeight: 400, color: theme.palette.text.primary, children: t('events.eventDetails.dashboardTab.editDashboard') })] }) }) }) }), _jsx(Box, { sx: { flex: 1, overflow: 'auto' }, children: dialogContent }), _jsxs(Box, { sx: styles.footerBoxStyle, children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', fullWidth: true, onClick: onClose, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', fullWidth: true, endIcon: _jsx(CheckOutlined, {}), onClick: handleSaveChanges, disabled: status === APIStatus.PENDING, children: t('actions.saveChanges') })] })] }));
    }
    return (_jsx(CustomDialog, { titleWithIcon: t('events.eventDetails.responsesTab.editResponsesColumns'), open: open, titleIcon: '/assets/images/dialog-pencil.png', onClose: onClose, maxWidth: 'md', actions: actionButtons, children: dialogContent }));
};
export default EditColumnDialog;
